<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <PrimeDialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    modal
    :header="
      isEditMode
        ? $t('vuesUtilisateurs.modifyViewPopup.title')
        : $t('vuesUtilisateurs.saveViewPopup.title')
    "
    :style="{ width: '550px', maxWidth: '95vw' }"
  >
    <PrimeStepper v-model:value="currentStep" :linear="!isEditMode">
      <PrimeStepList>
        <PrimeStep v-if="showScopeStep" value="1">{{
          $t("vuesUtilisateurs.saveViewPopup.scopeLabel")
        }}</PrimeStep>
        <PrimeStep :value="fileIdStep">{{
          $t("vuesUtilisateurs.saveViewPopup.fileIdLabel")
        }}</PrimeStep>
        <PrimeStep :value="detailsStep">{{
          $t("vuesUtilisateurs.saveViewPopup.detailsLabel")
        }}</PrimeStep>
      </PrimeStepList>
      <PrimeStepPanels>
        <!-- Step 1: Scope (skipped if globalOnly) -->
        <PrimeStepPanel
          v-if="showScopeStep"
          v-slot="{ activateCallback }"
          value="1"
        >
          <div class="flex flex-column gap-3 py-3">
            <p class="m-0 text-gray-600">
              {{ $t("vuesUtilisateurs.saveViewPopup.scopeDescription") }}
            </p>
            <PrimeSelectButton
              v-model="scope"
              :options="scopeOptions"
              optionLabel="label"
              optionValue="value"
              :disabled="isEditMode"
            />
          </div>
          <div class="flex justify-content-end pt-3">
            <PrimeButton
              :label="$t('next')"
              icon="ri-arrow-right-line"
              iconPos="right"
              @click="activateCallback(fileIdStep)"
              :disabled="!scope"
            />
          </div>
        </PrimeStepPanel>

        <!-- Step 2: File ID (global) / skipped for personal -->
        <PrimeStepPanel v-slot="{ activateCallback }" :value="fileIdStep">
          <div class="flex flex-column gap-3 py-3">
            <p class="m-0 text-gray-600" v-if="scope === 'global'">
              {{ $t("vuesUtilisateurs.saveViewPopup.fileIdDescription") }}
            </p>
            <PrimeInputGroup v-if="scope === 'global'">
              <PrimeInputGroupAddon>vue-</PrimeInputGroupAddon>
              <PrimeInputText
                v-model.trim="viewFileId"
                :placeholder="
                  $t('vuesUtilisateurs.saveViewPopup.fileIdPlaceholder')
                "
                :disabled="isEditMode"
              />
              <PrimeInputGroupAddon>.json</PrimeInputGroupAddon>
            </PrimeInputGroup>
            <p class="m-0 text-gray-600" v-else>
              {{ $t("vuesUtilisateurs.saveViewPopup.personalDescription") }}
            </p>
          </div>
          <div class="flex justify-content-between pt-3">
            <PrimeButton
              v-if="showScopeStep"
              :label="$t('previous')"
              icon="ri-arrow-left-line"
              severity="secondary"
              @click="activateCallback('1')"
            />
            <span v-else />
            <PrimeButton
              :label="$t('next')"
              icon="ri-arrow-right-line"
              iconPos="right"
              @click="activateCallback(detailsStep)"
              :disabled="scope === 'global' && !viewFileId"
            />
          </div>
        </PrimeStepPanel>

        <!-- Step 3: Name + Description -->
        <PrimeStepPanel v-slot="{ activateCallback }" :value="detailsStep">
          <div class="flex flex-column gap-3 py-3">
            <div class="flex flex-column gap-1">
              <label class="font-semibold">{{
                $t("vuesUtilisateurs.saveViewPopup.nameLabel")
              }}</label>
              <PrimeInputText
                v-model.trim="viewName"
                :class="{ 'p-invalid': errorMessage }"
                @input="$emit('clear-error')"
              />
              <small v-if="!viewName && !errorMessage" class="p-error">{{
                $t("vuesUtilisateurs.saveViewPopup.nameRequired")
              }}</small>
              <small v-if="errorMessage" class="p-error">{{
                errorMessage
              }}</small>
            </div>
            <div class="flex flex-column gap-1">
              <label class="font-semibold">{{
                $t("vuesUtilisateurs.saveViewPopup.descriptionLabel")
              }}</label>
              <PrimeTextarea v-model="viewDescription" rows="3" autoResize />
            </div>
          </div>
          <div class="flex justify-content-between pt-3">
            <PrimeButton
              :label="$t('previous')"
              icon="ri-arrow-left-line"
              severity="secondary"
              @click="activateCallback(fileIdStep)"
            />
            <PrimeButton
              :label="
                isEditMode
                  ? $t('vuesUtilisateurs.modifyView')
                  : $t('vuesUtilisateurs.saveView')
              "
              icon="ri-save-3-line"
              @click="save"
              :disabled="!isFormValid"
            />
          </div>
        </PrimeStepPanel>
      </PrimeStepPanels>
    </PrimeStepper>
  </PrimeDialog>
</template>

<script>
import { useUserStore } from "@/stores/user";
import { mapState, mapActions } from "pinia";
import Dialog from "primevue/dialog";
import Stepper from "primevue/stepper";
import StepList from "primevue/steplist";
import Step from "primevue/step";
import StepPanels from "primevue/steppanels";
import StepPanel from "primevue/steppanel";
import InputText from "primevue/inputtext";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import Textarea from "primevue/textarea";
import SelectButton from "primevue/selectbutton";
import Button from "primevue/button";

export default {
  name: "SaveViewPopup",
  components: {
    PrimeDialog: Dialog,
    PrimeStepper: Stepper,
    PrimeStepList: StepList,
    PrimeStep: Step,
    PrimeStepPanels: StepPanels,
    PrimeStepPanel: StepPanel,
    PrimeInputText: InputText,
    PrimeInputGroup: InputGroup,
    PrimeInputGroupAddon: InputGroupAddon,
    PrimeTextarea: Textarea,
    PrimeSelectButton: SelectButton,
    PrimeButton: Button,
  },
  props: {
    visible: Boolean,
    errorMessage: {
      type: String,
      default: null,
    },
    viewData: {
      type: Object,
      default: null,
    },
    globalOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:visible", "save-view", "clear-error"],
  data() {
    return {
      currentStep: "1",
      viewName: "",
      viewDescription: "",
      viewFileId: "",
      scope: "user",
    };
  },
  mounted() {
    this.checkPristyAdminStatus();
  },
  computed: {
    ...mapState(useUserStore, ["isPristyAdmin"]),
    isEditMode() {
      return !!this.viewData;
    },
    showScopeStep() {
      return this.isPristyAdmin && !this.globalOnly;
    },
    fileIdStep() {
      return this.showScopeStep ? "2" : "1";
    },
    detailsStep() {
      return this.showScopeStep ? "3" : "2";
    },
    scopeOptions() {
      const options = [
        {
          label: this.$t("vuesUtilisateurs.saveViewPopup.scopeUser"),
          value: "user",
        },
      ];
      if (this.isPristyAdmin) {
        options.push({
          label: this.$t("vuesUtilisateurs.saveViewPopup.scopeGlobal"),
          value: "global",
        });
      }
      return options;
    },
    isFormValid() {
      if (!this.viewName) {
        return false;
      }
      if (this.scope === "global" && !this.isEditMode && !this.viewFileId) {
        return false;
      }
      return true;
    },
  },
  methods: {
    ...mapActions(useUserStore, ["checkPristyAdminStatus"]),
    save() {
      if (!this.isFormValid) {
        return;
      }
      const payload = {
        name: this.viewName,
        description: this.viewDescription,
        scope: this.scope,
      };
      if (this.scope === "global") {
        const slug = this.viewFileId.toLowerCase().replace(/[^a-z0-9-]/g, "-");
        payload.filename = `vue-${slug}.json`;
      }
      this.$emit("save-view", payload);
    },
    resetForm() {
      this.viewName = "";
      this.viewDescription = "";
      this.viewFileId = "";
      this.scope = this.globalOnly ? "global" : "user";
      this.currentStep = this.globalOnly ? this.fileIdStep : "1";
      this.$emit("clear-error");
    },
    populateForm() {
      if (this.isEditMode) {
        this.viewName = this.viewData.name;
        this.viewDescription = this.viewData.description || "";
        this.scope = this.globalOnly ? "global" : this.viewData.scope;
        if (this.viewData.filename) {
          this.viewFileId = this.viewData.filename
            .replace(/^vue-/, "")
            .replace(/\.json$/, "");
        }
        // En mode édition, aller directement aux détails
        this.currentStep = this.detailsStep;
      } else {
        this.scope = this.globalOnly ? "global" : "user";
        this.currentStep = this.globalOnly ? this.fileIdStep : "1";
      }
    },
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        this.populateForm();
      } else {
        this.resetForm();
      }
    },
  },
};
</script>
